import React, { useCallback, useEffect, useRef, useState } from "react";
import { NodeViewWrapper } from "@tiptap/react";

const MIN_SIZE = 50;
const MAX_CONTENT_PADDING = 24;

/**
 * React-based resizable image for TipTap.
 * 8 handles: 4 corners (aspect-ratio locked) + 4 edges (free axis).
 */
const ResizableImageComponent = ({ node, updateAttributes, selected }) => {
  const { src, alt, title, width, height, align = "left" } = node.attrs;

  const imgRef = useRef(null);
  const latestSizeRef = useRef({ width: width || null, height: height || null });
  const [isResizing, setIsResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(width || null);
  const [currentHeight, setCurrentHeight] = useState(height || null);

  // Sync local state with node attrs when not actively resizing
  useEffect(() => {
    if (!isResizing) {
      setCurrentWidth(width || null);
      setCurrentHeight(height || null);
    }
  }, [width, height, isResizing]);

  useEffect(() => {
    latestSizeRef.current = { width: currentWidth, height: currentHeight };
  }, [currentWidth, currentHeight]);

  // Capture natural dimensions once the image loads
  const handleImageLoad = useCallback(() => {
    if (!currentWidth && imgRef.current) {
      setCurrentWidth(imgRef.current.naturalWidth);
      setCurrentHeight(imgRef.current.naturalHeight);
    }
  }, [currentWidth]);

  const handleResizeStart = useCallback(
    (e, direction) => {
      e.preventDefault();
      e.stopPropagation();

      const pointerEvent = e.nativeEvent || e;
      const startX = pointerEvent.clientX;
      const startY = pointerEvent.clientY;

      const rect = imgRef.current?.getBoundingClientRect();
      const startW = rect?.width || currentWidth || 300;
      const startH = rect?.height || currentHeight || 200;
      const aspectRatio = startW / startH;

      const editorRoot = imgRef.current?.closest(".tiptap.ProseMirror.simple-editor");
      const maxW = Math.max(MIN_SIZE, (editorRoot?.clientWidth || window.innerWidth) - MAX_CONTENT_PADDING);

      const isLeft = direction.includes("left");
      const isRight = direction.includes("right");
      const isTop = direction.includes("top");
      const isBottom = direction.includes("bottom");
      const isCorner = (isLeft || isRight) && (isTop || isBottom);
      const isHorizontalEdge = direction === "left" || direction === "right";
      const isVerticalEdge = direction === "top" || direction === "bottom";

      setIsResizing(true);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";

      const handlePointerMove = (moveEvent) => {
        const rawDX = moveEvent.clientX - startX;
        const rawDY = moveEvent.clientY - startY;

        // Flip deltas for left / top handles (dragging left = bigger)
        const dx = isLeft ? -rawDX : rawDX;
        const dy = isTop ? -rawDY : rawDY;

        let newW, newH;

        if (isCorner) {
          // Corner: preserve aspect ratio, use dominant axis
          if (Math.abs(dx) >= Math.abs(dy)) {
            newW = Math.max(MIN_SIZE, startW + dx);
            newH = newW / aspectRatio;
          } else {
            newH = Math.max(MIN_SIZE, startH + dy);
            newW = newH * aspectRatio;
          }
          // Re-clamp after ratio calc
          newW = Math.max(MIN_SIZE, newW);
          newH = Math.max(MIN_SIZE, newH);
        } else if (isHorizontalEdge) {
          // Left / Right edge: only width changes
          newW = Math.max(MIN_SIZE, startW + dx);
          newH = startH;
        } else if (isVerticalEdge) {
          // Top / Bottom edge: only height changes
          newW = startW;
          newH = Math.max(MIN_SIZE, startH + dy);
        } else {
          return;
        }

        newW = Math.min(maxW, newW);
        newH = Math.max(MIN_SIZE, newH);

        const roundedW = Math.round(newW);
        const roundedH = Math.round(newH);
        latestSizeRef.current = { width: roundedW, height: roundedH };
        setCurrentWidth(roundedW);
        setCurrentHeight(roundedH);
      };

      const stopResize = () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", stopResize);
        document.removeEventListener("pointercancel", stopResize);
        setIsResizing(false);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        // Commit final size to TipTap node attributes
        const finalW = latestSizeRef.current.width;
        const finalH = latestSizeRef.current.height;
        if (finalW && finalH) {
          updateAttributes({ width: finalW, height: finalH });
        }
      };

      document.addEventListener("pointermove", handlePointerMove, { passive: true });
      document.addEventListener("pointerup", stopResize);
      document.addEventListener("pointercancel", stopResize);
    },
    [currentWidth, currentHeight, updateAttributes]
  );

  const imgStyle = {
    width: currentWidth ? `${currentWidth}px` : "auto",
    height: currentHeight ? `${currentHeight}px` : "auto",
    display: "block",
    maxWidth: isResizing ? "none" : "100%",
  };

  const wrapperTextAlign =
    align === "center" ? "center" : align === "right" ? "right" : "left";

  const showHandles = selected || isResizing;

  return (
    <NodeViewWrapper
      className="resizable-image-wrapper"
      data-drag-handle=""
      style={{ display: "block", width: "100%", lineHeight: 0, textAlign: wrapperTextAlign }}
    >
      <div
        style={{ position: "relative", display: "inline-block", textAlign: "left" }}
        className={`resizable-image-container${isResizing ? " is-resizing" : ""}${selected ? " is-selected" : ""}`}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt || ""}
          title={title || undefined}
          data-align={align}
          style={imgStyle}
          onLoad={handleImageLoad}
          draggable={false}
        />

        {showHandles &&
          ALL_DIRECTIONS.map((dir) => (
            <Handle
              key={dir}
              position={dir}
              onPointerDown={(e) => handleResizeStart(e, dir)}
            />
          ))}
      </div>
    </NodeViewWrapper>
  );
};

// ─── Handle rendering ────────────────────────────────────────

const HANDLE = 10;
const HALF = HANDLE / 2;

const ALL_DIRECTIONS = [
  "top-left",
  "top",
  "top-right",
  "right",
  "bottom-right",
  "bottom",
  "bottom-left",
  "left",
];

const handleStyles = {
  // Corners
  "top-left":     { top: -HALF, left: -HALF, cursor: "nwse-resize" },
  "top-right":    { top: -HALF, right: -HALF, cursor: "nesw-resize" },
  "bottom-left":  { bottom: -HALF, left: -HALF, cursor: "nesw-resize" },
  "bottom-right": { bottom: -HALF, right: -HALF, cursor: "nwse-resize" },
  // Edges (centred along the edge)
  "top":    { top: -HALF, left: "50%", marginLeft: -HALF, cursor: "ns-resize" },
  "bottom": { bottom: -HALF, left: "50%", marginLeft: -HALF, cursor: "ns-resize" },
  "left":   { left: -HALF, top: "50%", marginTop: -HALF, cursor: "ew-resize" },
  "right":  { right: -HALF, top: "50%", marginTop: -HALF, cursor: "ew-resize" },
};

const Handle = ({ position, onPointerDown }) => (
  <div
    onPointerDown={onPointerDown}
    style={{
      position: "absolute",
      width: HANDLE,
      height: HANDLE,
      background: "#6366f1",
      border: "2px solid #fff",
      borderRadius: 2,
      boxShadow: "0 0 3px rgba(0,0,0,0.4)",
      zIndex: 20,
      ...handleStyles[position],
    }}
  />
);

export default ResizableImageComponent;
